import { GroupBy } from 'components/dashboard/StoreMetrics/types';
import { differenceInDays, format, parse, subMonths, subYears } from 'date-fns';
import { createContext, ReactNode, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { CustomerStatistics } from 'store/services/types';
import { useQuery } from 'utils/useQuery';
import { getMetrics, Metric } from './metrics';

interface ExecutiveSummaryContextProps {
  startDate: string;
  endDate: string;
  previousStartDate: string;
  previousEndDate: string;
  selectedCategory: Category;
  selectedTimeseriesMetric: Metric;
  selectedCategoryMetrics: Metric[];
  metricList: (keyof CustomerStatistics)[];
  groupBy: 'week' | 'day' | 'month';
  handleSelectGroupBy: (groupByOption: GroupBy) => void;
  getDateRangeGroupingOptions: (
    startDate: string,
    endDate: string
  ) => GroupBy[];
  groupByOptions: GroupBy[];
}

type Category =
  | 'overall'
  | 'acquisition'
  | 'activation'
  | 'retention'
  | 'churn';

export const ExecutiveSummaryContext = createContext(
  {} as ExecutiveSummaryContextProps
);

export const ExecutiveSummaryProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const query = useQuery();

  const selectedCategory = useMemo(
    () => (query.get('category') as Category) || 'overall',
    [query]
  );

  const selectedCategoryMetrics = useMemo(
    () => getMetrics(selectedCategory),
    [selectedCategory]
  );

  const timeseriesMetricId = query.get('timeseries_metric');

  const selectedTimeseriesMetric = useMemo(
    () =>
      selectedCategoryMetrics.find(
        (metric) => metric.id === timeseriesMetricId
      ) || selectedCategoryMetrics.filter((metric) => metric.hasTimeseries)[0],
    [selectedCategoryMetrics, timeseriesMetricId]
  );

  const startDate = useMemo(
    () =>
      query.get('start_date') || format(subMonths(new Date(), 1), 'yyyy-MM-dd'),
    [query]
  );

  const endDate = useMemo(
    () => query.get('end_date') || format(new Date(), 'yyyy-MM-dd'),
    [query]
  );

  const previousStartDate = useMemo(
    () =>
      format(
        subYears(parse(startDate, 'yyyy-MM-dd', Date.now()), 1),
        'yyyy-MM-dd'
      ),
    [startDate]
  );

  const previousEndDate = useMemo(
    () =>
      format(
        subYears(parse(endDate, 'yyyy-MM-dd', Date.now()), 1),
        'yyyy-MM-dd'
      ),
    [endDate]
  );

  const metricList = useMemo(
    () => getMetrics(selectedCategory).map(({ id }) => id),
    [selectedCategory]
  );

  const getDateRangeGroupingOptions = (startDate: string, endDate: string) => {
    if (
      differenceInDays(
        parse(endDate, 'yyyy-MM-dd', Date.now()),
        parse(startDate, 'yyyy-MM-dd', Date.now())
      ) < 90
    ) {
      return [GroupBy.Day];
    }

    if (
      differenceInDays(
        parse(endDate, 'yyyy-MM-dd', Date.now()),
        parse(startDate, 'yyyy-MM-dd', Date.now())
      ) < 365
    ) {
      return [GroupBy.Week, GroupBy.Day];
    }

    return [GroupBy.Month, GroupBy.Week, GroupBy.Day];
  };

  const getGroupByInitialState = () => {
    const groupBy = query.get('group_by');

    if (groupBy && Object.values(GroupBy).includes(groupBy as GroupBy)) {
      return groupBy as GroupBy;
    }

    if (startDate && endDate) {
      return getDateRangeGroupingOptions(startDate, endDate)[0];
    }

    return GroupBy.Day;
  };

  const [selectedGroupBy, setSelectedGroupBy] = useState<GroupBy>(
    getGroupByInitialState
  );

  const history = useHistory();

  const handleSelectGroupBy = (groupByOption: GroupBy) => {
    if (groupByOption) {
      setSelectedGroupBy(groupByOption);
      query.set('group_by', groupByOption);
      history.push({
        search: query.toString(),
      });
    }
  };

  useEffect(() => {
    if (!startDate || !endDate) {
      return;
    }

    const groupBy = getDateRangeGroupingOptions(startDate, endDate);

    if (groupBy[0] !== selectedGroupBy) {
      handleSelectGroupBy(groupBy[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const groupByOptions = useMemo(
    () => getDateRangeGroupingOptions(startDate, endDate),
    [startDate, endDate]
  );

  return (
    <ExecutiveSummaryContext.Provider
      value={{
        startDate,
        endDate,
        previousStartDate,
        previousEndDate,
        selectedCategory,
        selectedTimeseriesMetric,
        selectedCategoryMetrics,
        metricList,
        groupBy: selectedGroupBy,
        handleSelectGroupBy,
        getDateRangeGroupingOptions,
        groupByOptions,
      }}
    >
      {children}
    </ExecutiveSummaryContext.Provider>
  );
};
